import { Volume2 } from "lucide-react";

// TTS utility: read text aloud in selected language
const LANG_VOICES = {
  en: "en-US", nl: "nl-NL", fr: "fr-FR", de: "de-DE",
  pl: "pl-PL", ro: "ro-RO", ar: "ar-SA", zh: "zh-CN", it: "it-IT",
};

export function speakText(text, lang = "en") {
  if (!window.speechSynthesis || !text) return;
  window.speechSynthesis.cancel();
  const utter = new SpeechSynthesisUtterance(text);
  utter.lang = LANG_VOICES[lang] || "en-US";
  utter.rate = 0.95;
  utter.pitch = 1;
  window.speechSynthesis.speak(utter);
}

export function stopSpeaking() {
  if (window.speechSynthesis) window.speechSynthesis.cancel();
}

// Read Aloud button component
export function ReadAloudButton({ text, lang, className = "" }) {
  const handleClick = (e) => {
    e.stopPropagation();
    if (window.speechSynthesis.speaking) {
      stopSpeaking();
    } else {
      speakText(text, lang);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider bg-slate-100 hover:bg-lime-100 text-slate-500 hover:text-lime-700 transition-all active:scale-95 ${className}`}
      data-testid="read-aloud-btn"
      title="Read aloud"
    >
      <Volume2 className="w-3 h-3" />
    </button>
  );
}

// Tutorial chapter IDs mapped to translation keys
export const TUTORIALS = {
  planner: {
    titleKey: "tut_planner_title",
    chapters: [
      { id: "p1", titleKey: "tut_p1_title", textKey: "tut_p1_text", trigger: "dashboard-load" },
      { id: "p2", titleKey: "tut_p2_title", textKey: "tut_p2_text", trigger: "import-data-btn" },
      { id: "p3", titleKey: "tut_p3_title", textKey: "tut_p3_text", trigger: "site-row" },
      { id: "p4", titleKey: "tut_p4_title", textKey: "tut_p4_text", trigger: "confirm-transport" },
      { id: "p5", titleKey: "tut_p5_title", textKey: "tut_p5_text", trigger: "pdf-export-btn" },
      { id: "p6", titleKey: "tut_p6_title", textKey: "tut_p6_text", trigger: "live-feed" },
      { id: "p7", titleKey: "tut_p7_title", textKey: "tut_p7_text", trigger: "calendar-btn" },
      { id: "p8", titleKey: "tut_p8_title", textKey: "tut_p8_text", trigger: "route-builder-btn" },
      { id: "p9", titleKey: "tut_p9_title", textKey: "tut_p9_text", trigger: "add-machine-btn" },
      { id: "p10", titleKey: "tut_p10_title", textKey: "tut_p10_text", trigger: "site-row" },
    ],
  },
  driver: {
    titleKey: "tut_driver_title",
    chapters: [
      { id: "d1", titleKey: "tut_d1_title", textKey: "tut_d1_text", trigger: "toggle-shift-btn" },
      { id: "d2", titleKey: "tut_d2_title", textKey: "tut_d2_text", trigger: "deliver" },
      { id: "d3", titleKey: "tut_d3_title", textKey: "tut_d3_text", trigger: "feedback-modal" },
      { id: "d4", titleKey: "tut_d4_title", textKey: "tut_d4_text", trigger: "driver-machines" },
      { id: "d5", titleKey: "tut_d5_title", textKey: "tut_d5_text", trigger: "driver-messages-btn" },
      { id: "d6", titleKey: "tut_d6_title", textKey: "tut_d6_text", trigger: "driver-site" },
      { id: "d7", titleKey: "tut_d7_title", textKey: "tut_d7_text", trigger: "assigned-route" },
      { id: "d8", titleKey: "tut_d8_title", textKey: "tut_d8_text", trigger: "driver-calendar-btn" },
    ],
  },
  machinist: {
    titleKey: "tut_machinist_title",
    chapters: [
      { id: "m1", titleKey: "tut_m1_title", textKey: "tut_m1_text", trigger: "site-selection" },
      { id: "m2", titleKey: "tut_m2_title", textKey: "tut_m2_text", trigger: "fuel-btn" },
      { id: "m3", titleKey: "tut_m3_title", textKey: "tut_m3_text", trigger: "edit-site-btn" },
      { id: "m4", titleKey: "tut_m4_title", textKey: "tut_m4_text", trigger: "machine-status" },
      { id: "m5", titleKey: "tut_m5_title", textKey: "tut_m5_text", trigger: "add-rental-btn" },
      { id: "m6", titleKey: "tut_m6_title", textKey: "tut_m6_text", trigger: "site-phase-select" },
    ],
  },
  service: {
    titleKey: "tut_service_title",
    chapters: [
      { id: "s1", titleKey: "tut_s1_title", textKey: "tut_s1_text", trigger: "dashboard-load" },
      { id: "s2", titleKey: "tut_s2_title", textKey: "tut_s2_text", trigger: "service-req" },
      { id: "s3", titleKey: "tut_s3_title", textKey: "tut_s3_text", trigger: "resolve-btn" },
    ],
  },
  transport: {
    titleKey: "tut_transport_title",
    chapters: [
      { id: "t1", titleKey: "tut_t1_title", textKey: "tut_t1_text", trigger: "dashboard-load" },
      { id: "t2", titleKey: "tut_t2_title", textKey: "tut_t2_text", trigger: "transport-req" },
      { id: "t3", titleKey: "tut_t3_title", textKey: "tut_t3_text", trigger: "confirm-transport" },
    ],
  },
  viewer: {
    titleKey: "tut_viewer_title",
    chapters: [
      { id: "v1", titleKey: "tut_v1_title", textKey: "tut_v1_text", trigger: "dashboard-load" },
    ],
  },
};
